import type {
  Assumptions,
  FundingScenario,
  FundingSource,
  LoanKind,
} from '../types';

export interface FundingResult {
  /** Total cash delivered to fund the purchase, after tax. */
  netDelivered: number;
  /** Sum of gross outflows (pre-tax) across all selling sources. */
  grossOutflow: number;
  /** Total tax / penalty paid. */
  totalTax: number;
  /** Per-bucket pulls to apply to the projection at the purchase year.
   *  These are the actual dollars removed from each bucket (including tax). */
  pullHysa: number;
  pullTaxable: number;
  pullRetirement: number;
  /** Home-sale outputs, applied at the purchase year. */
  homeSold: boolean;
  homeSalePrice: number;
  homeMortgagePayoff: number; // reported separately for UI; engine reads mortgage state itself
  homeSaleTax: number;
  homeSaleNet: number;
  /** Mortgage outputs, applied from the purchase year forward. */
  newMortgagePrincipal: number;
  newMortgageMonthlyPI: number;
  newMortgageAnnualRate: number;
  newMortgageTermYears: number;
  /** Non-mortgage loans (auto/personal/HELOC/401k) to add to the engine's
   *  active-loans list when this funding is applied. */
  newLoans: NewLoan[];
  /** Cash flow this year that comes out of savings (reduces contributions). */
  cashFlow: number;
  /** Purchase amount the plan falls short of covering, given the net delivered. */
  unfunded: number;
  warnings: string[];
}

export interface NewLoan {
  id: string;
  label: string;
  loanKind: LoanKind;
  principal: number;
  annualRate: number;
  termYears: number;
  monthlyPI: number;
  /** ID of the event that created this loan (set by the engine, not funding). */
  sourceEventId?: string;
  /** Display name of that event — "New car", "Kitchen renovation", etc. */
  sourceEventName?: string;
}

export interface FundingContext {
  /** Current outstanding mortgage balance at the purchase year.
   *  Needed so a home_sale source can pay off the old loan. Provided by projection. */
  currentMortgageBalance: number;
  /** Age at the purchase year. Used to decide pre-59½ penalty. */
  ageAtPurchase: number;
  /** Purchase amount (the target outflow). */
  purchaseAmount: number;
  /** Whether the projection is in a working (contributing) year. */
  working: boolean;
}

export function computeFunding(
  scenario: FundingScenario | null,
  assumptions: Assumptions,
  ctx: FundingContext,
): FundingResult {
  const result: FundingResult = {
    netDelivered: 0,
    grossOutflow: 0,
    totalTax: 0,
    pullHysa: 0,
    pullTaxable: 0,
    pullRetirement: 0,
    homeSold: false,
    homeSalePrice: 0,
    homeMortgagePayoff: 0,
    homeSaleTax: 0,
    homeSaleNet: 0,
    newMortgagePrincipal: 0,
    newMortgageMonthlyPI: 0,
    newMortgageAnnualRate: 0,
    newMortgageTermYears: 0,
    newLoans: [],
    cashFlow: 0,
    unfunded: 0,
    warnings: [],
  };
  if (!scenario) {
    result.unfunded = ctx.purchaseAmount;
    return result;
  }

  const stateFed = assumptions.stateTaxRate;
  const section121Amount = assumptions.filingStatus === 'married' ? 500_000 : 250_000;

  for (const src of scenario.sources) {
    applySource(src, assumptions, ctx, result, stateFed, section121Amount);
  }

  // Gap vs. the target purchase amount
  const gap = ctx.purchaseAmount - result.netDelivered;
  if (gap > 0.5) {
    result.unfunded = gap;
    result.warnings.push(
      `Plan is short of the purchase amount by ${formatMoney(gap)} — add another source.`,
    );
  }

  return result;
}

function applySource(
  src: FundingSource,
  a: Assumptions,
  ctx: FundingContext,
  r: FundingResult,
  stateFed: number,
  section121Amount: number,
): void {
  switch (src.type) {
    case 'hysa': {
      r.pullHysa += src.amount;
      r.grossOutflow += src.amount;
      r.netDelivered += src.amount;
      return;
    }
    case 'taxable_ltcg': {
      const gain = src.grossAmount * Math.max(0, Math.min(1, src.gainFraction));
      const tax = gain * (a.ltcgRate + stateFed);
      r.pullTaxable += src.grossAmount;
      r.grossOutflow += src.grossAmount;
      r.totalTax += tax;
      // Tax is paid out-of-pocket — modeled as additional HYSA drain.
      r.pullHysa += tax;
      r.netDelivered += src.grossAmount - tax;
      return;
    }
    case 'taxable_stcg': {
      const gain = src.grossAmount * Math.max(0, Math.min(1, src.gainFraction));
      const tax = gain * (a.ordinaryRate + stateFed);
      r.pullTaxable += src.grossAmount;
      r.grossOutflow += src.grossAmount;
      r.totalTax += tax;
      r.pullHysa += tax;
      r.netDelivered += src.grossAmount - tax;
      return;
    }
    case 'retirement_pretax': {
      const isEarly = ctx.ageAtPurchase < 59.5;
      const penalty = isEarly ? a.earlyWithdrawalPenalty : 0;
      const totalRate = a.ordinaryRate + stateFed + penalty;
      const tax = src.grossAmount * totalRate;
      r.pullRetirement += src.grossAmount;
      r.grossOutflow += src.grossAmount;
      r.totalTax += tax;
      r.pullHysa += tax;
      r.netDelivered += src.grossAmount - tax;
      if (isEarly) {
        r.warnings.push(
          `401(k) pre-59½ withdrawal: ordinary income tax + 10% penalty (effective ${(totalRate * 100).toFixed(0)}%).`,
        );
      }
      return;
    }
    case 'retirement_roth': {
      // Treat as tax-free principal withdrawal — assume user knows.
      r.pullRetirement += src.amount;
      r.grossOutflow += src.amount;
      r.netDelivered += src.amount;
      return;
    }
    case 'cash_flow': {
      r.cashFlow += src.amount;
      r.netDelivered += src.amount;
      return;
    }
    case 'home_sale': {
      const costBasis = src.costBasisOverride ?? a.homeCostBasis;
      const payoff = Math.max(0, ctx.currentMortgageBalance);
      const gain = Math.max(0, src.salePrice - costBasis);
      const excludable = src.useSection121 ? Math.min(gain, section121Amount) : 0;
      const taxableGain = Math.max(0, gain - excludable);
      const tax = taxableGain * (a.ltcgRate + stateFed);
      const net = Math.max(0, src.salePrice - payoff - tax);
      r.homeSold = true;
      r.homeSalePrice = src.salePrice;
      r.homeMortgagePayoff = payoff;
      r.homeSaleTax = tax;
      r.homeSaleNet = net;
      r.totalTax += tax;
      // Net proceeds fund the purchase directly (no bucket pull).
      r.netDelivered += net;
      if (!src.useSection121 && gain > 0) {
        r.warnings.push(
          'Section 121 primary-residence exclusion is OFF for this home sale — capital gain is fully taxable.',
        );
      }
      return;
    }
    case 'mortgage': {
      const p = Math.max(0, src.principal);
      const rate = Math.max(0, src.annualRate);
      const term = Math.max(1, src.termYears);
      const monthlyPI = computeMonthlyPI(p, rate, term);
      r.newMortgagePrincipal += p;
      r.newMortgageMonthlyPI += monthlyPI;
      r.newMortgageAnnualRate = rate;
      r.newMortgageTermYears = term;
      r.netDelivered += p;
      return;
    }
    case 'loan': {
      const p = Math.max(0, src.principal);
      const rate = Math.max(0, src.annualRate);
      const term = Math.max(1, src.termYears);
      const monthlyPI = computeMonthlyPI(p, rate, term);
      const label = src.label || defaultLoanLabel(src.loanKind);
      r.newLoans.push({
        id: src.id,
        label,
        loanKind: src.loanKind,
        principal: p,
        annualRate: rate,
        termYears: term,
        monthlyPI,
      });
      r.netDelivered += p;
      if (src.loanKind === 'retirement_401k') {
        // The borrower's own 401(k) fronts the cash — that principal is
        // temporarily out of the retirement bucket. Engine re-credits as
        // principal is repaid.
        r.pullRetirement += p;
        // Typical 401(k) loan limit: the lesser of $50k or 50% of vested
        // balance. The engine doesn't know the vested balance here, so we
        // only flag on the absolute $50k ceiling.
        if (p > 50_000) {
          r.warnings.push(
            `401(k) loan of ${formatMoney(p)} exceeds the $50,000 IRS maximum; engine will simulate it anyway but it may be illegal in practice.`,
          );
        }
        if (term > 5) {
          r.warnings.push(
            `401(k) loans must typically be repaid within 5 years (longer terms only for a principal-residence loan). Double-check this is eligible.`,
          );
        }
        r.warnings.push(
          `401(k) loan: opportunity cost is modeled by keeping the principal out of the retirement bucket until repaid. If you separate from your employer before payoff, the balance converts to a taxable distribution (not modeled).`,
        );
      } else if (src.loanKind === 'heloc') {
        r.warnings.push(
          `HELOC interest may be tax-deductible only if used for qualifying home improvements (TCJA 2017+). The engine does not currently claim that deduction.`,
        );
      }
      return;
    }
  }
}

function computeMonthlyPI(principal: number, annualRate: number, termYears: number): number {
  const m = annualRate / 12;
  const n = termYears * 12;
  return m === 0
    ? principal / n
    : (principal * m * Math.pow(1 + m, n)) / (Math.pow(1 + m, n) - 1);
}

function defaultLoanLabel(kind: LoanKind): string {
  switch (kind) {
    case 'auto': return 'Auto loan';
    case 'personal': return 'Personal loan';
    case 'heloc': return 'HELOC';
    case 'retirement_401k': return '401(k) loan';
  }
}

function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}
